import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  FaEnvelope,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaChevronRight,
  FaTimes,
  FaPaperPlane,
  FaCheckCircle,
} from "react-icons/fa";
import { MdSupportAgent, MdFeedback, MdContactSupport } from "react-icons/md";
import { useUserLocation } from "../../utils/useUserLocation";
import Footer from "../../files/Footer/Footer";
import Header from "../../files/Header";
import emailjs from "emailjs-com";

const HelpCenter = () => {
  // Contact methods data
  const contactMethods = [
    {
      title: "Email",
      value: "support@nearmeind.com",
      icon: <FaEnvelope />,
      color: "#673AB7",
      url: "mailto:support@nearmeind.com",
    },
    {
      title: "Phone",
      value: "+91 8544104411",
      icon: <FaPhoneAlt />,
      color: "#009688",
      url: "tel:+918544104411",
    },
    {
      title: "Corporate Office",
      value:
        "B2/1805, 18th Floor, Panchsheel Greens 2, GH01A, Sector 16, Greater Noida, 201306",
      icon: <FaMapMarkerAlt />,
      color: "#FF9800",
      url: "https://maps.google.com/?q=NearMe%20India,%20B2/1805%2018th%20Floor,%20Panchsheel,%20Greens%202,%20GH01A,%20Sector%2016,%20Greater%20Noida,%20201306",
    },
  ];

  // FAQ data
  const faqs = [
    {
      question: "How do I reset my password?",
      answer:
        "You can reset your password by clicking on 'Forgot Password' on the login page. We'll send a password reset link to your registered email address.",
    },
    {
      question: "What are your business hours?",
      answer:
        "Our customer support team is available 24/7. For sales inquiries, our business hours are Monday-Friday, 9:00 AM to 6:00 PM IST.",
    },
    {
      question: "How can I update my account information?",
      answer:
        "You can update your account information in the 'My Profile' section of your account settings. Some details may require verification.",
    },
    {
      question: "What payment methods do you accept?",
      answer:
        "We accept all major credit/debit cards, UPI payments, net banking, and popular digital wallets.",
    },
    {
      question: "How do I track my order?",
      answer:
        "Once your order is shipped, you'll receive a tracking number via email and SMS. You can track it in the 'My Orders' section of your account.",
    },
  ];

  // State management
  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(() => {
    return localStorage.getItem("selectedCategory") || "products";
  });
  const [showContactForm, setShowContactForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);

  // Location hook
  const {
    location: currentLocation,
    error: locationError,
    isLoading: isLocationLoading,
    updateLocation,
  } = useUserLocation();

  // Save selected category to localStorage
  useEffect(() => {
    localStorage.setItem("selectedCategory", selectedCategory);
  }, [selectedCategory]);

  // Handle modal close
  const handleModalClose = (shouldClose, newCoordinates = null) => {
    setShowModal(false);
    if (newCoordinates) {
      updateLocation({
        coordinates: {
          latitude: newCoordinates.latitude,
          longitude: newCoordinates.longitude,
        },
        address: newCoordinates.address || "",
        details: newCoordinates.details || null,
      });
    }
  };

  // Launch URLs
  const launchURL = (url) => {
    window.open(url, "_blank");
  };

  // Toggle FAQ item
  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = "Name is required";
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Please enter a valid email";
    }
    if (!formData.subject.trim()) errors.subject = "Subject is required";
    if (!formData.message.trim()) errors.message = "Message is required";
    return errors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);
    setFormErrors({});

    try {
      // Replace with your EmailJS service ID, template ID, and user ID
      await emailjs.send(
        "service_your_service_id",
        "template_your_template_id",
        formData,
        "your_user_id"
      );

      setSubmitSuccess(true);
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });

      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
        setShowContactForm(false);
      }, 5000);
    } catch (error) {
      setFormErrors({
        submit: "Failed to send message. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header
        userLocation={currentLocation}
        setShowModal={setShowModal}
        setUserLocation={updateLocation}
        showModal={showModal}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
      />

      <HelpCenterContainer>
        <GradientHeader>
          <HeaderContent>
            <BackButton onClick={() => window.history.back()}>
              &larr; Back
            </BackButton>
            <HeaderTitle>
              <MdSupportAgent /> Help Center
            </HeaderTitle>
            <HeaderSubtitle>
              We're here to help you with any questions or issues
            </HeaderSubtitle>
          </HeaderContent>
        </GradientHeader>

        <MainContent>
          <HelpCard>
            <CardTitle>How can we help you today?</CardTitle>
            <CardDescription>
              Choose from the options below or contact our support team
              directly.
            </CardDescription>

            <HelpOptions>
              <OptionCard onClick={() => setShowContactForm(true)}>
                <OptionIcon color="#4a00e0">
                  <MdContactSupport />
                </OptionIcon>
                <OptionTitle>Contact Support</OptionTitle>
                <OptionDescription>
                  Send us a message and we'll get back to you within 24 hours
                </OptionDescription>
              </OptionCard>

              <OptionCard onClick={() => launchURL("tel:+918544104411")}>
                <OptionIcon color="#009688">
                  <FaPhoneAlt />
                </OptionIcon>
                <OptionTitle>Call Us</OptionTitle>
                <OptionDescription>
                  Speak directly with our support team 24/7
                </OptionDescription>
              </OptionCard>

              <OptionCard onClick={() => launchURL(contactMethods[2].url)}>
                <OptionIcon color="#FF9800">
                  <FaMapMarkerAlt />
                </OptionIcon>
                <OptionTitle>Visit Us</OptionTitle>
                <OptionDescription>
                  Find our corporate office location
                </OptionDescription>
              </OptionCard>
            </HelpOptions>

            <ContactMethods>
              {contactMethods.map((method, index) => (
                <ContactCard
                  key={index}
                  color={method.color}
                  onClick={() => launchURL(method.url)}
                >
                  <ContactIcon color={method.color}>{method.icon}</ContactIcon>
                  <ContactInfo>
                    <ContactTitle>{method.title}</ContactTitle>
                    <ContactValue>{method.value}</ContactValue>
                  </ContactInfo>
                  <ContactArrow>
                    <FaChevronRight />
                  </ContactArrow>
                </ContactCard>
              ))}
            </ContactMethods>
          </HelpCard>

          <FAQSection>
            <SectionTitle>Frequently Asked Questions</SectionTitle>
            <FAQList>
              {faqs.map((faq, index) => (
                <FAQItem key={index} active={activeFaq === index}>
                  <FAQQuestion onClick={() => toggleFaq(index)}>
                    {faq.question}
                    <FAQToggle>{activeFaq === index ? "−" : "+"}</FAQToggle>
                  </FAQQuestion>
                  <FAQAnswer active={activeFaq === index}>
                    {faq.answer}
                  </FAQAnswer>
                </FAQItem>
              ))}
            </FAQList>
          </FAQSection>

          <FeedbackSection>
            <SectionTitle>Share Your Feedback</SectionTitle>
            <FeedbackPrompt>
              We value your input! Let us know how we can improve your
              experience.
            </FeedbackPrompt>
            <FeedbackButton onClick={() => setShowContactForm(true)}>
              <MdFeedback /> Give Feedback
            </FeedbackButton>
          </FeedbackSection>
        </MainContent>

        {/* Contact Form Modal */}
        {showContactForm && (
          <ModalOverlay>
            <ContactFormModal>
              <ModalHeader>
                <ModalTitle>Contact Our Support Team</ModalTitle>
                <CloseButton onClick={() => setShowContactForm(false)}>
                  <FaTimes />
                </CloseButton>
              </ModalHeader>

              {submitSuccess ? (
                <SuccessMessage>
                  <FaCheckCircle />
                  <h3>Thank you for contacting us!</h3>
                  <p>
                    Your message has been sent successfully. We'll get back to
                    you soon.
                  </p>
                </SuccessMessage>
              ) : (
                <Form onSubmit={handleSubmit}>
                  <FormGroup>
                    <Label htmlFor="name">Your Name</Label>
                    <Input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      error={!!formErrors.name}
                    />
                    {formErrors.name && (
                      <ErrorText>{formErrors.name}</ErrorText>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      error={!!formErrors.email}
                    />
                    {formErrors.email && (
                      <ErrorText>{formErrors.email}</ErrorText>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="subject">Subject</Label>
                    <Input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      error={!!formErrors.subject}
                    />
                    {formErrors.subject && (
                      <ErrorText>{formErrors.subject}</ErrorText>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="message">Your Message</Label>
                    <TextArea
                      id="message"
                      name="message"
                      rows="5"
                      value={formData.message}
                      onChange={handleInputChange}
                      error={!!formErrors.message}
                    />
                    {formErrors.message && (
                      <ErrorText>{formErrors.message}</ErrorText>
                    )}
                  </FormGroup>

                  {formErrors.submit && (
                    <ErrorText style={{ textAlign: "center" }}>
                      {formErrors.submit}
                    </ErrorText>
                  )}

                  <SubmitButton type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      "Sending..."
                    ) : (
                      <>
                        <FaPaperPlane /> Send Message
                      </>
                    )}
                  </SubmitButton>
                </Form>
              )}
            </ContactFormModal>
          </ModalOverlay>
        )}
      </HelpCenterContainer>

      <Footer />
    </>
  );
};

// Styled Components
const HelpCenterContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(to bottom, #f5f5f5 0%, #eceff1 100%);
`;

const GradientHeader = styled.header`
  background: linear-gradient(135deg, #ed7a07 0%, #ff4d00 100%);
  color: white;
  padding: 2rem 1rem 3rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 10;
  text-align: center;
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-size: 1rem;
  cursor: pointer;
  align-self: flex-start;
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-radius: 50px;
  transition: all 0.3s ease;
  margin-bottom: 1rem;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 2.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-weight: 700;

  @media (min-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeaderSubtitle = styled.p`
  margin: 0.5rem 0 0;
  font-size: 1.1rem;
  opacity: 0.9;
`;

const MainContent = styled.main`
  max-width: 1200px;
  margin: -3rem auto 0;
  padding: 1rem;
  position: relative;
  z-index: 5;

  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const HelpCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.08);
  margin-bottom: 2.5rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const CardTitle = styled.h2`
  color: #333;
  font-size: 2rem;
  margin: 0 0 1rem;
  font-weight: 700;
  text-align: center;

  @media (min-width: 768px) {
    font-size: 2.2rem;
  }
`;

const CardDescription = styled.p`
  color: #666;
  font-size: 1.1rem;
  line-height: 1.6;
  margin: 0 auto 2rem;
  text-align: center;
  max-width: 700px;
`;

const HelpOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0 3rem;
`;

const OptionCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid #eee;
  text-align: center;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    border-color: #ddd;
  }
`;

const OptionIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${(props) => props.color}15;
  color: ${(props) => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin: 0 auto 1rem;
  border: 2px solid ${(props) => props.color}30;
`;

const OptionTitle = styled.h3`
  margin: 0 0 0.5rem;
  font-size: 1.3rem;
  color: #333;
`;

const OptionDescription = styled.p`
  margin: 0;
  color: #666;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const ContactMethods = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 2rem 0;
`;

const ContactCard = styled.div`
  display: flex;
  align-items: center;
  padding: 1.5rem;
  border-radius: 12px;
  background: ${(props) => props.color}08;
  border: 1px solid ${(props) => props.color}20;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px ${(props) => props.color}15;
    background: ${(props) => props.color}10;
  }

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 5px;
    background: ${(props) => props.color};
  }
`;

const ContactIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: ${(props) => props.color};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  flex-shrink: 0;
  margin-right: 1.5rem;
  box-shadow: 0 4px 10px ${(props) => props.color}50;

  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
    font-size: 1.1rem;
    margin-right: 1rem;
  }
`;

const ContactInfo = styled.div`
  flex: 1;
`;

const ContactTitle = styled.h3`
  margin: 0 0 0.3rem;
  font-size: 1.2rem;
  color: #333;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const ContactValue = styled.p`
  margin: 0;
  color: #666;
  font-size: 1rem;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const ContactArrow = styled.div`
  color: #999;
  font-size: 1.2rem;
  margin-left: 1rem;
`;

const FAQSection = styled.section`
  background: white;
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.08);
  margin-bottom: 2.5rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  color: #333;
  font-size: 1.8rem;
  margin: 0 0 1.5rem;
  font-weight: 700;
  text-align: center;
`;

const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FAQItem = styled.div`
  border-radius: 8px;
  background: ${(props) => (props.active ? "#f9f9f9" : "white")};
  border: 1px solid ${(props) => (props.active ? "#ddd" : "#eee")};
  overflow: hidden;
  transition: all 0.3s ease;
`;

const FAQQuestion = styled.div`
  padding: 1.2rem 1.5rem;
  color: #333;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease;

  &:hover {
    background: #f9f9f9;
  }
`;

const FAQToggle = styled.span`
  font-size: 1.3rem;
  color: #666;
`;

const FAQAnswer = styled.div`
  padding: ${(props) => (props.active ? "1rem 1.5rem 1.5rem" : "0 1.5rem")};
  color: #666;
  font-size: 1rem;
  line-height: 1.6;
  max-height: ${(props) => (props.active ? "500px" : "0")};
  overflow: hidden;
  transition: all 0.3s ease;
  border-top: ${(props) => (props.active ? "1px solid #eee" : "none")};
`;

const FeedbackSection = styled.div`
  background: linear-gradient(135deg, #f5b87bff 0%, #ed7a07 100%);
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  text-align: center;
  color: white;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const FeedbackPrompt = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  margin: 0 auto 1.5rem;
  max-width: 700px;
  opacity: 0.9;
`;

const FeedbackButton = styled.button`
  background: white;
  color: #ed7a07;
  border: none;
  border-radius: 50px;
  padding: 0.8rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);

  &:hover {
  background:#ed7a07;
  color: white
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ContactFormModal = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
  animation: modalFadeIn 0.3s ease;

  @keyframes modalFadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.5rem;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 1.3rem;
  cursor: pointer;
  padding: 0.5rem;
  transition: all 0.2s ease;

  &:hover {
    color: #333;
    transform: rotate(90deg);
  }
`;

const Form = styled.form`
  padding: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #555;
  font-size: 0.95rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem 1rem;
  border: 1px solid ${(props) => (props.error ? "#ff4444" : "#ddd")};
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #4a00e0;
    box-shadow: 0 0 0 3px rgba(74, 0, 224, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.8rem 1rem;
  border: 1px solid ${(props) => (props.error ? "#ff4444" : "#ddd")};
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #4a00e0;
    box-shadow: 0 0 0 3px rgba(74, 0, 224, 0.1);
  }
`;

const ErrorText = styled.span`
  color: #ff4444;
  font-size: 0.85rem;
  margin-top: 0.3rem;
  display: block;
`;

const SubmitButton = styled.button`
  background: #4a00e0;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 1rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.3s ease;

  &:hover {
    background: #3a00c0;
  }

  &:disabled {
    background: #aaa;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: #333;

  svg {
    font-size: 3rem;
    color: #4caf50;
    margin-bottom: 1rem;
  }

  h3 {
    margin: 0 0 0.5rem;
    font-size: 1.5rem;
  }

  p {
    margin: 0;
    color: #666;
    font-size: 1rem;
  }
`;

export default HelpCenter;
